import { Injectable } from '@angular/core';
import { DocumentBridgeGtmService } from '@core/services/google-tag-manager.service';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { of } from 'rxjs';
import { catchError, exhaustMap, groupBy, map, mergeMap, switchMap, tap } from 'rxjs/operators';
import { FileHelper } from '@shared/helpers/file.helper';
import * as DocumentOverviewActions from '../actions/document-overview.actions';
import * as DocumentRequestBatchActions from '../actions/document-request-batch.actions';
import { ClientDocumentGroup, ClientDocumentOverviewCursors } from '../models/document-overview.models';
import { DocumentOverviewService } from '../services/document-overview.service';
import { DocumentRequestBatchService } from '../services/document-request-batch.service';

const CLIENT_DOCUMENT_CURSOR_PARAM: Record<ClientDocumentGroup, keyof ClientDocumentOverviewCursors> = {
  requested: 'requestedAfter',
  reupload_needed: 'reuploadNeededAfter',
  awaiting_approval: 'awaitingApprovalAfter',
  approved: 'approvedAfter',
};

@Injectable()
export class DocumentRequestBatchEffects {
  public downloadGeneratedFiles$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DocumentRequestBatchActions.downloadGeneratedFiles),
      switchMap(({ batchId }) =>
        this.service.getGeneratedFilesZip$(batchId).pipe(
          map((response) => {
            if (!response || !response.archive_file_url) {
              throw new Error('DOCUMENT_REQUEST_BATCHES.ERRORS.FAILED_TO_RETRIEVE_LINK');
            }
            return DocumentRequestBatchActions.downloadGeneratedFilesSuccess({ response });
          }),
          catchError((error) =>
            of(
              DocumentRequestBatchActions.downloadGeneratedFilesError({
                error: error.message || 'DOCUMENT_REQUEST_BATCHES.ERRORS.PREPARATION_FAILED',
              })
            )
          )
        )
      )
    )
  );

  public downloadClientGeneratedFiles$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DocumentRequestBatchActions.downloadClientGeneratedFiles),
      switchMap(() =>
        this.service.getClientGeneratedFilesZip$().pipe(
          map((response) => {
            if (!response || !response.archive_file_url) {
              throw new Error('DOCUMENT_REQUEST_BATCHES.ERRORS.FAILED_TO_RETRIEVE_LINK');
            }
            return DocumentRequestBatchActions.downloadClientGeneratedFilesSuccess({ response });
          }),
          catchError((error) =>
            of(
              DocumentRequestBatchActions.downloadClientGeneratedFilesError({
                error: error.message || 'DOCUMENT_REQUEST_BATCHES.ERRORS.PREPARATION_FAILED',
              })
            )
          )
        )
      )
    )
  );

  public downloadGeneratedFilesSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(DocumentRequestBatchActions.downloadGeneratedFilesSuccess),
        tap(({ response }) => {
          FileHelper.downloadFileByUrl(response.archive_file_url, response.file_name);
          this.gtm.pushDownloadSuccessTag('batch');
        })
      ),
    { dispatch: false }
  );

  public downloadGeneratedFilesError$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(DocumentRequestBatchActions.downloadGeneratedFilesError),
        tap(({ error }) => {
          this.gtm.pushDownloadErrorTag(error, 'batch');
        })
      ),
    { dispatch: false }
  );

  public downloadClientGeneratedFilesSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(DocumentRequestBatchActions.downloadClientGeneratedFilesSuccess),
        tap(({ response }) => {
          FileHelper.downloadFileByUrl(response.archive_file_url, response.file_name);
          this.gtm.pushDownloadSuccessTag('client');
        })
      ),
    { dispatch: false }
  );

  public downloadClientGeneratedFilesError$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(DocumentRequestBatchActions.downloadClientGeneratedFilesError),
        tap(({ error }) => {
          this.gtm.pushDownloadErrorTag(error, 'client');
        })
      ),
    { dispatch: false }
  );

  public loadDocumentOverview$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DocumentOverviewActions.loadDocumentOverview),
      switchMap(({ batchId }) =>
        this.documentOverviewService.getDocumentOverview$(batchId).pipe(
          map((overview) => DocumentOverviewActions.loadDocumentOverviewSuccess({ overview })),
          catchError((error) =>
            of(
              DocumentOverviewActions.loadDocumentOverviewError({
                error: error.message || 'DOCUMENT_REQUEST_BATCHES.ERRORS.FAILED_TO_LOAD_OVERVIEW',
              })
            )
          )
        )
      )
    )
  );

  public loadClientDocumentOverview$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DocumentOverviewActions.loadClientDocumentOverview),
      switchMap(() =>
        this.documentOverviewService.getClientDocumentOverview$().pipe(
          map((overview) => DocumentOverviewActions.loadClientDocumentOverviewSuccess({ overview })),
          catchError((error) =>
            of(
              DocumentOverviewActions.loadClientDocumentOverviewError({
                error: error.message || 'DOCUMENT_REQUEST_BATCHES.ERRORS.FAILED_TO_LOAD_OVERVIEW',
              })
            )
          )
        )
      )
    )
  );

  public loadMoreClientDocuments$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DocumentOverviewActions.loadMoreClientDocuments),
      groupBy(({ group }) => group),
      mergeMap((group$) =>
        group$.pipe(
          exhaustMap(({ group, after }) =>
            this.documentOverviewService.getClientDocumentOverview$({ [CLIENT_DOCUMENT_CURSOR_PARAM[group]]: after }).pipe(
              map((overview) => DocumentOverviewActions.loadMoreClientDocumentsSuccess({ group, bucket: overview[group] })),
              catchError((error) =>
                of(
                  DocumentOverviewActions.loadMoreClientDocumentsError({
                    group,
                    error: error.message || 'DOCUMENT_REQUEST_BATCHES.ERRORS.FAILED_TO_LOAD_MORE',
                  })
                )
              )
            )
          )
        )
      )
    )
  );

  public loadMoreClientDocumentsError$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(DocumentOverviewActions.loadMoreClientDocumentsError),
        tap(({ error }) => {
          this.gtm.pushLoadMoreErrorTag(error);
        })
      ),
    { dispatch: false }
  );

  constructor(
    private readonly actions$: Actions,
    private readonly service: DocumentRequestBatchService,
    private readonly documentOverviewService: DocumentOverviewService,
    private readonly gtm: DocumentBridgeGtmService
  ) {}
}
